import { styled } from "goober";
import type { ReactNode } from "react";
import { useUrlSort } from "../../hooks/useUrlSort";
import { routes } from "../../lib/urls";
import { GhostPill } from "../GhostPill";
import { ListTable } from "../ListTable";
import { Text } from "../Text";
import {
  type MonitoringInteractionName,
  useMonitoringInteraction,
} from "./analytics";
import { NameBody, NameCell, ScrollArea, TableCard } from "./monitoringList";

export const GhostBadge = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--alpha8);
  flex-shrink: 0;
`;

const GHOST_NAME_WIDTHS = [112, 88, 136, 104, 124];

const EmptyMessage = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

export type BreakdownColumn<K extends string> = {
  key: K;
  label: string;
  format: (value: number | null) => string;
};

export type BreakdownRow<K extends string> = {
  key: string;
  link?: string;
  targetId?: string;
  name: ReactNode;
  values: Record<K, number | null>;
};

type Props<K extends string> = {
  nameHeader: string;
  columns: readonly BreakdownColumn<K>[];
  rows: readonly BreakdownRow<K>[];
  emptyMessage: string;
  loading?: boolean;
  sortEvent: MonitoringInteractionName;
  linkEvent: MonitoringInteractionName;
};

export function AiBreakdownTable<K extends string>({
  nameHeader,
  columns,
  rows,
  emptyMessage,
  loading = false,
  sortEvent,
  linkEvent,
}: Props<K>) {
  const recordInteraction = useMonitoringInteraction();
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.monitoring.ai,
    Object.fromEntries(columns.map((column) => [column.key, "desc"])) as Record<
      K,
      "desc"
    >
  );
  if (!loading && rows.length === 0) {
    return (
      <TableCard>
        <EmptyMessage>
          <Text variant="text-small" color="var(--text-secondary)">
            {emptyMessage}
          </Text>
        </EmptyMessage>
      </TableCard>
    );
  }

  const sortKey = sortColumn ?? ("cost" as K);
  const dir = sortDirection === "asc" ? 1 : -1;
  const sorted = [...rows].sort(
    (a, b) =>
      dir * ((a.values[sortKey] ?? 0) - (b.values[sortKey] ?? 0)) ||
      a.key.localeCompare(b.key)
  );

  const items: ({ key: string; link?: string } & Record<
    string,
    ReactNode | (() => void)
  >)[] = loading
    ? GHOST_NAME_WIDTHS.map((width, index) => ({
        key: `ghost-${index}`,
        name: (
          <NameCell aria-hidden>
            <GhostBadge />
            <NameBody>
              <GhostPill style={{ width }} />
            </NameBody>
          </NameCell>
        ),
        ...Object.fromEntries(
          columns.map((column) => [
            column.key,
            <GhostPill aria-hidden style={{ width: 40 }} />,
          ])
        ),
      }))
    : sorted.map((row) => ({
        key: row.key,
        link: row.link,
        onNavigate: () =>
          recordInteraction(linkEvent, {
            targetId: row.targetId,
          }),
        name: row.name,
        ...Object.fromEntries(
          columns.map((column) => [
            column.key,
            <Text variant="text-small">
              {column.format(row.values[column.key])}
            </Text>,
          ])
        ),
      }));

  return (
    <TableCard aria-hidden={loading || undefined}>
      <ScrollArea>
        <ListTable
          headers={{
            name: nameHeader,
            ...Object.fromEntries(
              columns.map((column) => [column.key, column.label])
            ),
          }}
          columns={{
            name: "flex",
            ...Object.fromEntries(columns.map((column) => [column.key, "md"])),
          }}
          sortableColumns={loading ? [] : sortableColumns}
          sortColumn={loading ? null : sortColumn}
          sortDirection={sortDirection}
          onSort={
            loading
              ? undefined
              : (column) => {
                  recordInteraction(sortEvent, { value: column });
                  handleSort(column);
                }
          }
          items={items}
        />
      </ScrollArea>
    </TableCard>
  );
}
